/**
 * ManageDeployDialog — manage-deploy-versioning M5
 *
 * Site-independent deploy dialog for the smartparking-manage / -backend
 * services. The site-batch flow (DeployConfirmDialog) is intentionally
 * separate because manage services run on a single host (192.168.0.102)
 * and don't participate in per-site rollouts.
 *
 * Flow:
 *   1. Operator picks which manage service to deploy
 *   2. Picks Build (new SemVer) or Reuse (existing tag)
 *   3. VersionInputField fetches lastVersion + auto-prefills minor +1
 *   4. Submit → POST /v0.1/deployments/trigger { appname, version, mode, ... }
 *
 * The backend pre-creates a deployment_results row and dispatches deploy.yaml.
 * Polling/observation is the parent's responsibility (e.g. via existing
 * useDeploymentPolling / useActiveDeployment hooks).
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Typography,
  CircularProgress,
} from '@mui/material';
import { RocketLaunch as RocketIcon } from '@mui/icons-material';
import deploymentService from '../../services/DeploymentService';
import VersionInputField, { type DeployMode } from './VersionInputField';
import type { ManageAppname } from '../../models/Deployment';

export interface ManageDeployDialogProps {
  open: boolean;
  onClose: () => void;
  /** Project ID required by the trigger API contract (validates project row). */
  projectId: string;
  branch?: string; // default 'develop'
  /** Optional: parent receives the deploymentId after successful dispatch. */
  onTriggered?: (deploymentId: number) => void;
}

const MANAGE_APPNAMES: { value: ManageAppname; label: string }[] = [
  { value: 'smartparking-manage', label: '관리 프론트' },
  { value: 'smartparking-manage-backend', label: '관리 백엔드' },
];

const ManageDeployDialog: React.FC<ManageDeployDialogProps> = ({
  open,
  onClose,
  projectId,
  branch = 'develop',
  onTriggered,
}) => {
  const [appname, setAppname] = useState<ManageAppname>('smartparking-manage');
  const [mode, setMode] = useState<DeployMode>('build');
  const [version, setVersion] = useState<string>('');
  const [versionValid, setVersionValid] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset transient state on open so a fresh dialog never shows previous error.
  useEffect(() => {
    if (open) {
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  // When user switches appname, clear version so VersionInputField re-prefills.
  const handleAppnameChange = useCallback((next: ManageAppname) => {
    setAppname(next);
    setVersion('');
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!versionValid) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await deploymentService.triggerDeployment({
        projectId,
        branch,
        appname,
        // build: send `version`. reuse: send `targetVersion` instead.
        ...(mode === 'build'
          ? { version: version.trim(), mode: 'build' as const }
          : { targetVersion: version.trim(), mode: 'reuse' as const }),
      });
      if (res?.data?.deploymentId && onTriggered) {
        onTriggered(res.data.deploymentId);
      }
      onClose();
    } catch (e: any) {
      const msg = e?.response?.data?.message ?? e?.message ?? '배포 트리거 실패';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [appname, mode, version, versionValid, projectId, branch, onClose, onTriggered]);

  const submitDisabled = submitting || !versionValid;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>관리 서비스 배포</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <div>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
              배포 대상
            </Typography>
            <ToggleButtonGroup
              value={appname}
              exclusive
              onChange={(_, next: ManageAppname | null) => next && handleAppnameChange(next)}
              size="small"
              disabled={submitting}
              fullWidth
            >
              {MANAGE_APPNAMES.map((opt) => (
                <ToggleButton key={opt.value} value={opt.value} sx={{ flex: 1 }}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </div>

          <VersionInputField
            appname={appname}
            mode={mode}
            version={version}
            onModeChange={setMode}
            onVersionChange={setVersion}
            onValidChange={setVersionValid}
            disabled={submitting}
          />

          {error && (
            <Alert severity="error" variant="outlined">
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          취소
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : <RocketIcon />
          }
          onClick={handleSubmit}
          disabled={submitDisabled}
        >
          {submitting ? '배포 시작 중...' : mode === 'reuse' ? '재배포' : '빌드 + 배포'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageDeployDialog;
